import React, { useState, useRef, useMemo } from 'react';
import LocalFilterPanel from '../../../components/common/LocalFilterPanel';
import { Popover, PopoverTrigger, PopoverContent } from '../../../components/ui/popover';

// ── Shared sub-components ──────────────────────────────────────────────────

const PALETTE = [
  { hex: '#22c55e', label: 'Green' },
  { hex: '#eab308', label: 'Yellow' },
  { hex: '#f97316', label: 'Orange' },
  { hex: '#ef4444', label: 'Red' },
  { hex: '#6366f1', label: 'Default' },
];

const INTERVAL_OPTIONS = [
  { value: 'day', label: 'Day' },
  { value: 'month', label: 'Month' },
  { value: 'year', label: 'Year' },
];

const intervalLabel = (v) => ({ day: 'Day', month: 'Month', year: 'Year' }[v] || v);

const ColorPicker = ({ value, onChange, label }) => {
  const inputRef = useRef(null);
  const isCustom = !PALETTE.some((p) => p.hex === value);
  return (
    <div>
      {label && <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>}
      <div className="flex items-center gap-1.5 flex-wrap">
        {PALETTE.map(({ hex, label: l }) => (
          <button key={hex} onClick={() => onChange(hex)} title={l} type="button"
            className={`w-7 h-7 rounded-full transition-all cursor-pointer ${value === hex ? 'ring-2 ring-offset-2 ring-gray-600 scale-110' : 'opacity-75 hover:opacity-100 hover:scale-110'}`}
            style={{ backgroundColor: hex }} />
        ))}
        {isCustom && (
          <button onClick={() => inputRef.current?.click()} title={value} type="button"
            className="w-7 h-7 rounded-full ring-2 ring-offset-2 ring-gray-600 scale-110 cursor-pointer"
            style={{ backgroundColor: value }} />
        )}
        <div className="relative w-7 h-7">
          <button type="button" title="Custom color"
            className="w-7 h-7 rounded-full border border-gray-300 bg-white flex items-center justify-center hover:bg-gray-50 transition-colors text-gray-500 cursor-pointer">
            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="13.5" cy="6.5" r=".5" fill="currentColor" />
              <circle cx="17.5" cy="10.5" r=".5" fill="currentColor" />
              <circle cx="8.5" cy="7.5" r=".5" fill="currentColor" />
              <circle cx="6.5" cy="12.5" r=".5" fill="currentColor" />
              <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" />
            </svg>
          </button>
          <input ref={inputRef} type="color" value={value} onChange={(e) => onChange(e.target.value)}
            className="absolute inset-0 opacity-0 w-full h-full cursor-pointer" tabIndex={-1} />
        </div>
      </div>
    </div>
  );
};

const IntervalSelect = ({ value, onChange, disabled, label }) => (
  <div>
    {label && <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>}
    <div className="flex gap-1">
      {INTERVAL_OPTIONS.map((opt) => (
        <button key={opt.value} type="button" disabled={disabled} onClick={() => onChange(opt.value)}
          className={`flex-1 py-1.5 text-xs font-medium rounded border transition-colors ${
            value === opt.value
              ? 'bg-primary-600 text-white border-primary-600'
              : 'bg-white text-gray-600 border-gray-300 hover:border-primary-400 hover:text-primary-600'
          } disabled:opacity-50 disabled:cursor-not-allowed`}>
          {opt.label}
        </button>
      ))}
    </div>
  </div>
);

const ThresholdSection = ({ unit, enabled, thresholdValue, colorBelow, colorAbove, onEnabledChange, onThresholdValueChange, onColorBelowChange, onColorAboveChange, loading }) => {
  const [inputStr, setInputStr] = useState(String(thresholdValue ?? 0));
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <label className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors">
        <input type="checkbox" checked={enabled} onChange={(e) => onEnabledChange(e.target.checked)}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" disabled={loading} />
        <span className="text-xs font-semibold text-gray-700">Threshold color</span>
      </label>
      {enabled && (
        <div className="px-3 py-3 space-y-3 bg-white">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Threshold value ({unit})</label>
            <input type="number" min="0" step="any" value={inputStr}
              onChange={(e) => { const raw = e.target.value; setInputStr(raw); if (raw === '') return; const p = parseFloat(raw); if (!isNaN(p)) onThresholdValueChange(Math.max(0, p)); }}
              onBlur={() => { const p = parseFloat(inputStr); const s = isNaN(p) ? 0 : Math.max(0, p); setInputStr(String(s)); onThresholdValueChange(s); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" disabled={loading} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <ColorPicker value={colorBelow} onChange={onColorBelowChange} label="Below threshold" />
            <ColorPicker value={colorAbove} onChange={onColorAboveChange} label="At threshold (≥)" />
          </div>
        </div>
      )}
    </div>
  );
};

// Small ⓘ icon button that opens a Popover with rich content
const InfoButton = ({ label, children }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button type="button" aria-label={`More information about ${label}`}
        className="inline-flex items-center text-gray-400 hover:text-gray-600 transition-colors ml-1 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded-full">
        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
        </svg>
      </button>
    </PopoverTrigger>
    <PopoverContent side="top" align="start" className="w-80 text-xs leading-relaxed p-3 space-y-2">
      {children}
    </PopoverContent>
  </Popover>
);

// ── Main component ─────────────────────────────────────────────────────────

const AutomationPotentialKPISettings = ({
  title,
  objectTypes,
  objectTypeActivities,
  selectedObjectType,
  selectedActivity,
  effortPerOccurrence,
  manualDisplayInterval,
  monetaryEnabled,
  fullyLoadedHourlyRate,
  monetaryDisplayInterval,
  color,
  manualThresholdEnabled,
  manualThresholdValue,
  manualColorBelow,
  manualColorAbove,
  monetaryThresholdEnabled,
  monetaryThresholdValue,
  monetaryColorBelow,
  monetaryColorAbove,
  localFilters,
  updateLocalFilter,
  countData,
  manualPotential,
  manualIntervalLabel,
  onTitleChange,
  onObjectTypeChange,
  onActivityChange,
  onEffortChange,
  onManualDisplayIntervalChange,
  onMonetaryEnabledChange,
  onFullyLoadedHourlyRateChange,
  onMonetaryDisplayIntervalChange,
  onColorChange,
  onManualThresholdEnabledChange,
  onManualThresholdValueChange,
  onManualColorBelowChange,
  onManualColorAboveChange,
  onMonetaryThresholdEnabledChange,
  onMonetaryThresholdValueChange,
  onMonetaryColorBelowChange,
  onMonetaryColorAboveChange,
  loading = false,
}) => {
  const activitiesForType = (objectTypeActivities || {})[selectedObjectType] || [];

  // Numeric input string states
  const [effortInput, setEffortInput] = useState(String(effortPerOccurrence ?? 0));
  const [rateInput, setRateInput] = useState(String(fullyLoadedHourlyRate ?? 45));

  // Salary calculator state
  const [calcOpen, setCalcOpen] = useState(false);
  const [grossSalary, setGrossSalary] = useState('');
  const [weeklyHours, setWeeklyHours] = useState('40');
  const [overheadFactor, setOverheadFactor] = useState('1.25');

  // Salary calculator validation
  const weeklyHoursNum = parseFloat(weeklyHours);
  const overheadFactorNum = parseFloat(overheadFactor);
  const weeklyHoursError = weeklyHours !== '' && (isNaN(weeklyHoursNum) || weeklyHoursNum < 1 || weeklyHoursNum > 80)
    ? 'Must be between 1 and 80.' : null;
  const overheadError = overheadFactor !== '' && (isNaN(overheadFactorNum) || overheadFactorNum < 1.0 || overheadFactorNum > 3.0)
    ? 'Must be between 1.0 and 3.0.' : null;

  const calculatedRate = useMemo(() => {
    const s = parseFloat(grossSalary);
    const h = parseFloat(weeklyHours) || 40;
    const f = parseFloat(overheadFactor) || 1.25;
    if (!s || s <= 0 || h <= 0 || f <= 0) return null;
    return (s * f) / (h * (52 / 12));
  }, [grossSalary, weeklyHours, overheadFactor]);

  const handleUseRate = () => {
    if (calculatedRate == null) return;
    const rounded = Math.round(calculatedRate * 100) / 100;
    onFullyLoadedHourlyRateChange(rounded);
    setRateInput(String(rounded));
    setCalcOpen(false);
  };

  // Live monetary preview — recomputes using the monetary interval so switching Day/Hour/Year updates the number
  const rateNum = parseFloat(rateInput);
  const previewFreq = useMemo(() => {
    if (!countData) return null;
    if (monetaryDisplayInterval === 'day') return countData.per_day;
    if (monetaryDisplayInterval === 'month') return countData.per_month;
    if (monetaryDisplayInterval === 'year') return countData.per_year;
    return null;
  }, [countData, monetaryDisplayInterval]);
  const previewHours = (previewFreq != null && effortPerOccurrence != null) ? (previewFreq * effortPerOccurrence) / 60 : null;
  const previewValue = (previewHours != null && !isNaN(rateNum) && rateNum > 0) ? previewHours * rateNum : null;

  const rateError = monetaryEnabled && (isNaN(rateNum) || rateNum <= 0)
    ? 'Please enter a valid hourly rate greater than 0.' : null;

  return (
    <div className="space-y-6">

      {/* ── Title ── */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
        <input type="text" value={title} onChange={(e) => onTitleChange(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Automation Potential" disabled={loading} />
      </div>

      {/* ── Configuration ── */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
        <label className="block text-sm font-medium text-gray-700">Configuration</label>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Object Type *</label>
          <select value={selectedObjectType} onChange={(e) => onObjectTypeChange(e.target.value)}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" disabled={loading}>
            <option value="">Select object type</option>
            {(objectTypes || []).map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Activity *</label>
          <select value={selectedActivity} onChange={(e) => onActivityChange(e.target.value)}
            className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            disabled={loading || !selectedObjectType}>
            <option value="">Select activity</option>
            {activitiesForType.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
          {!selectedObjectType && <p className="text-xs text-gray-400 mt-1">Please select an object type first</p>}
        </div>
      </div>

      {/* ── Manual Automation Potential ── */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Manual Automation Potential</label>
          <p className="text-xs text-gray-500 mt-0.5">Estimated manual effort saved by automating this activity</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Effort per occurrence (min)</label>
          <input type="number" min="0" step="0.1" value={effortInput}
            onChange={(e) => { const raw = e.target.value; setEffortInput(raw); if (raw === '') return; const p = parseFloat(raw); if (!isNaN(p)) onEffortChange(Math.max(0, p)); }}
            onBlur={() => { const p = parseFloat(effortInput); const s = isNaN(p) ? 0 : Math.max(0, p); setEffortInput(String(s)); onEffortChange(s); }}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" disabled={loading} />
        </div>
        <IntervalSelect label="Output interval" value={manualDisplayInterval} onChange={onManualDisplayIntervalChange} disabled={loading} />

        {/* Manual preview */}
        {(() => {
          const freq = !countData ? null
            : manualDisplayInterval === 'day' ? countData.per_day
            : manualDisplayInterval === 'month' ? countData.per_month
            : manualDisplayInterval === 'year' ? countData.per_year
            : null;
          const hours = (freq != null && effortPerOccurrence != null) ? (freq * effortPerOccurrence) / 60 : null;
          return (
            <div className="rounded-md bg-gray-100 px-3 py-2.5">
              {hours != null ? (
                <p className="text-xs text-gray-500">
                  Preview:{' '}
                  <span className="font-medium text-gray-700">
                    {freq.toLocaleString('de-DE', { maximumFractionDigits: 2 })} occ./{intervalLabel(manualDisplayInterval)}
                  </span>
                  {' '}× {effortPerOccurrence} min ÷ 60 ={' '}
                  <span className="font-semibold text-gray-800">
                    {hours.toLocaleString('de-DE', { maximumFractionDigits: 2 })} h/{intervalLabel(manualDisplayInterval)}
                  </span>
                </p>
              ) : (
                <p className="text-xs text-gray-400 italic">
                  Configure object type and activity to see the preview.
                </p>
              )}
            </div>
          );
        })()}

        <ThresholdSection
          unit={`h/${intervalLabel(manualDisplayInterval)}`}
          enabled={manualThresholdEnabled} thresholdValue={manualThresholdValue}
          colorBelow={manualColorBelow} colorAbove={manualColorAbove}
          onEnabledChange={onManualThresholdEnabledChange} onThresholdValueChange={onManualThresholdValueChange}
          onColorBelowChange={onManualColorBelowChange} onColorAboveChange={onManualColorAboveChange} loading={loading} />
      </div>

      {/* ── Monetary Potential ── */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={monetaryEnabled} onChange={(e) => onMonetaryEnabledChange(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500" disabled={loading} />
          <span className="text-sm font-medium text-gray-700">Monetary Potential</span>
        </label>

        {monetaryEnabled && (
          <div className="space-y-4">

            {/* Fully Loaded Hourly Rate */}
            <div>
              <div className="flex items-center mb-1">
                <label className="text-xs font-medium text-gray-600">Fully Loaded Hourly Rate</label>
                <InfoButton label="Fully Loaded Hourly Rate">
                  <p className="font-semibold text-gray-800 mb-1">What is the Fully Loaded Hourly Rate?</p>
                  <p className="text-gray-600">The <strong>total cost per hour</strong> this activity generates — not just gross wage, but including employer social contributions, paid leave, and overhead.</p>
                  <table className="w-full mt-2 border-collapse text-gray-600">
                    <thead><tr className="border-b border-gray-200"><th className="text-left py-1 font-medium">Role</th><th className="text-right py-1 font-medium">Typical range</th></tr></thead>
                    <tbody>
                      <tr className="border-b border-gray-100"><td className="py-1">Office / admin</td><td className="text-right py-1">€30 – €45 / h</td></tr>
                      <tr className="border-b border-gray-100"><td className="py-1">Skilled professional</td><td className="text-right py-1">€45 – €70 / h</td></tr>
                      <tr><td className="py-1">Senior / team lead</td><td className="text-right py-1">€70 – €100 / h</td></tr>
                    </tbody>
                  </table>
                  <p className="text-gray-500 mt-2"><strong>Rule of thumb:</strong> gross monthly salary × 1.25 ÷ 160 working hours.</p>
                  <p className="text-gray-400 mt-1 italic">Example: €3,500/month → €3,500 × 1.25 ÷ 160 ≈ <strong>€27 / h</strong></p>
                </InfoButton>
              </div>
              <div className="flex items-center gap-2">
                <input type="number" min="0" step="0.5" value={rateInput} placeholder="e.g. 45"
                  onChange={(e) => { const raw = e.target.value; setRateInput(raw); if (raw === '') return; const p = parseFloat(raw); if (!isNaN(p) && p > 0) onFullyLoadedHourlyRateChange(p); }}
                  onBlur={() => { const p = parseFloat(rateInput); const s = isNaN(p) || p <= 0 ? fullyLoadedHourlyRate : p; setRateInput(String(s)); onFullyLoadedHourlyRateChange(s); }}
                  className={`flex-1 px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${rateError ? 'border-red-400 focus:ring-red-400' : 'border-gray-300'}`}
                  aria-describedby="rate-error" disabled={loading} />
                <span className="text-xs text-gray-500 whitespace-nowrap">€ / h</span>
              </div>
              {rateError && <p id="rate-error" className="text-xs text-red-500 mt-1">{rateError}</p>}
            </div>

            {/* Salary calculator */}
            <div>
              <button type="button" onClick={() => setCalcOpen((v) => !v)}
                className="flex items-center gap-1.5 text-xs text-primary-600 hover:text-primary-800 transition-colors focus:outline-none"
                aria-expanded={calcOpen}>
                <svg className={`w-3 h-3 transition-transform duration-200 ${calcOpen ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                Calculate from salary instead
              </button>

              <div style={{ maxHeight: calcOpen ? '400px' : '0', overflow: 'hidden', transition: 'max-height 0.25s ease' }}>
                <div className="mt-3 bg-blue-50 border border-blue-100 rounded-lg p-3 space-y-3">

                  {/* Gross Monthly Salary */}
                  <div>
                    <div className="flex items-center mb-1">
                      <label className="text-xs font-medium text-gray-600">Gross Monthly Salary</label>
                      <InfoButton label="Gross Monthly Salary">
                        <p className="text-gray-600">The <strong>gross monthly salary</strong> of the employee performing this activity — the figure on the employment contract, before taxes and deductions.</p>
                        <p className="text-gray-500 mt-1">For a team with different salaries, enter a representative average.</p>
                      </InfoButton>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="number" min="0" step="100" value={grossSalary} placeholder="e.g. 3,500"
                        onChange={(e) => setGrossSalary(e.target.value)}
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white" />
                      <span className="text-xs text-gray-500 whitespace-nowrap">€ / month</span>
                    </div>
                  </div>

                  {/* Weekly Working Hours */}
                  <div>
                    <div className="flex items-center mb-1">
                      <label className="text-xs font-medium text-gray-600">Weekly Working Hours</label>
                      <InfoButton label="Weekly Working Hours">
                        <p className="text-gray-600">The contractual weekly working hours of the employee.</p>
                        <p className="text-gray-500 mt-1">Standard full-time in Germany: <strong>40 h / week</strong> (~160 h / month).</p>
                      </InfoButton>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="number" min="1" max="80" step="1" value={weeklyHours} placeholder="40"
                        onChange={(e) => setWeeklyHours(e.target.value)}
                        className={`flex-1 px-3 py-1.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white ${weeklyHoursError ? 'border-red-400' : 'border-gray-300'}`} />
                      <span className="text-xs text-gray-500 whitespace-nowrap">h / week</span>
                    </div>
                    {weeklyHoursError && <p className="text-xs text-red-500 mt-1">{weeklyHoursError}</p>}
                  </div>

                  {/* Overhead Factor */}
                  <div>
                    <div className="flex items-center mb-1">
                      <label className="text-xs font-medium text-gray-600">Overhead Factor</label>
                      <InfoButton label="Overhead Factor">
                        <p className="text-gray-600">Accounts for employer costs <strong>on top of gross salary</strong>: social security, statutory insurance, sick leave provisions, etc.</p>
                        <table className="w-full mt-2 border-collapse text-gray-600">
                          <thead><tr className="border-b border-gray-200"><th className="text-left py-1 font-medium">Coverage</th><th className="text-right py-1 font-medium">Factor</th></tr></thead>
                          <tbody>
                            <tr className="border-b border-gray-100"><td className="py-1">Employer social contributions (DE)</td><td className="text-right py-1">1.21 – 1.25</td></tr>
                            <tr><td className="py-1">+ General overhead (office, IT, HR)</td><td className="text-right py-1">1.40 – 1.70</td></tr>
                          </tbody>
                        </table>
                        <p className="text-gray-500 mt-2">For most SMEs, <strong>1.25</strong> is a solid starting point.</p>
                      </InfoButton>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="number" min="1" max="3" step="0.01" value={overheadFactor} placeholder="1.25"
                        onChange={(e) => setOverheadFactor(e.target.value)}
                        className={`flex-1 px-3 py-1.5 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white ${overheadError ? 'border-red-400' : 'border-gray-300'}`} />
                    </div>
                    {overheadError && <p className="text-xs text-red-500 mt-1">{overheadError}</p>}
                  </div>

                  {/* Calculated result + CTA */}
                  <div className="flex items-center justify-between pt-1 border-t border-blue-100">
                    <span className="text-xs text-gray-500">
                      Calculated rate:{' '}
                      <span className="font-semibold text-gray-700">
                        {calculatedRate != null ? `€${calculatedRate.toFixed(2)} / h` : '—'}
                      </span>
                    </span>
                    <button type="button" onClick={handleUseRate} disabled={calculatedRate == null || !!weeklyHoursError || !!overheadError}
                      className="px-3 py-1 text-xs font-medium bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                      Use this rate
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Output interval */}
            <IntervalSelect label="Output interval" value={monetaryDisplayInterval} onChange={onMonetaryDisplayIntervalChange} disabled={loading} />

            {/* Live preview */}
            <div className="rounded-md bg-gray-100 px-3 py-2.5">
              {previewValue != null ? (
                <p className="text-xs text-gray-500">
                  Preview:{' '}
                  <span className="font-medium text-gray-700">
                    {previewHours?.toLocaleString('de-DE', { maximumFractionDigits: 2 })} h/{intervalLabel(monetaryDisplayInterval)}
                  </span>
                  {' '}× €{rateNum.toFixed(2)}/h ={' '}
                  <span className="font-semibold text-gray-800">
                    €{previewValue.toLocaleString('de-DE', { maximumFractionDigits: 2 })} / {intervalLabel(monetaryDisplayInterval)}
                  </span>
                </p>
              ) : (
                <p className="text-xs text-gray-400 italic">
                  Configure &ldquo;Manual Automation Potential&rdquo; above to see the preview.
                </p>
              )}
            </div>

            {/* Threshold */}
            <ThresholdSection
              unit={`€/${intervalLabel(monetaryDisplayInterval)}`}
              enabled={monetaryThresholdEnabled} thresholdValue={monetaryThresholdValue}
              colorBelow={monetaryColorBelow} colorAbove={monetaryColorAbove}
              onEnabledChange={onMonetaryThresholdEnabledChange} onThresholdValueChange={onMonetaryThresholdValueChange}
              onColorBelowChange={onMonetaryColorBelowChange} onColorAboveChange={onMonetaryColorAboveChange} loading={loading} />
          </div>
        )}
      </div>

      {/* ── Display color ── */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700">Display color</label>
          <p className="text-xs text-gray-500 mt-0.5">Used when no threshold color is active</p>
        </div>
        <ColorPicker value={color} onChange={onColorChange} />
      </div>

      {/* ── Local filters ── */}
      {localFilters !== undefined && (
        <LocalFilterPanel localFilters={localFilters} updateLocalFilter={updateLocalFilter} activeFile={undefined} />
      )}
    </div>
  );
};

export default AutomationPotentialKPISettings;
